import React, { useState } from 'react';
import { Pressable, View } from 'react-native';
import { fmt, useAppStore } from '@restorant/core';
import type { Category, MenuItem } from '@restorant/core';
import { radius, useColors } from '../theme';
import { useItemsLabel, useT } from '../i18n';
import { useCategories, useCategoryLabel, useItemName, useMenuItems } from '../menu';
import { Icon } from '../icons/Icon';
import type { IconName } from '../icons/Icon';
import { CategoryEditor } from '../components/CategoryEditor';
import { ItemEditor } from '../components/ItemEditor';
import { T } from '../components/Text';

function ItemRow({ item, onEdit }: { item: MenuItem; onEdit: () => void }) {
  const colors = useColors();
  const itemName = useItemName();
  const prices = Object.entries(item.prices);

  return (
    <Pressable
      onPress={onEdit}
      style={({ hovered }: any) => ({
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
        paddingVertical: 11,
        paddingHorizontal: 12,
        borderRadius: radius.md,
        borderWidth: 1,
        borderColor: hovered ? colors.goldDim : 'transparent',
        backgroundColor: hovered ? colors.surface2 : 'transparent',
        opacity: item.active ? 1 : 0.5,
      })}
    >
      <View style={{ flex: 1, gap: 3, minWidth: 0 }}>
        <T size={13.5} weight={600} numberOfLines={1}>
          {itemName(item)}
        </T>
        <T size={11.5} color={colors.textFaint} numberOfLines={1}>
          {prices.map(([l, p]) => (l === 'Price' ? fmt(p) : `${l} ${fmt(p)}`)).join(' · ')}
        </T>
      </View>
      {!item.active ? <Icon name="cloudOff" size={14} color={colors.textFaint} /> : null}
      <Icon name="edit" size={15} color={colors.textFaint} />
    </Pressable>
  );
}

function CategoryBlock({
  cat,
  onEditCategory,
  onAddItem,
  onEditItem,
}: {
  cat: Category;
  onEditCategory: () => void;
  onAddItem: () => void;
  onEditItem: (item: MenuItem) => void;
}) {
  const colors = useColors();
  const t = useT();
  const items = useItemsLabel();
  const catLabel = useCategoryLabel();
  const all = useMenuItems(true).filter((i) => i.cat === cat.id);

  return (
    <View
      style={{
        borderWidth: 1,
        borderColor: colors.line,
        borderRadius: radius.lg,
        backgroundColor: colors.surface,
        marginBottom: 12,
        overflow: 'hidden',
      }}
    >
      <Pressable
        onPress={onEditCategory}
        style={({ hovered }: any) => ({
          flexDirection: 'row',
          alignItems: 'center',
          gap: 11,
          padding: 14,
          backgroundColor: hovered ? colors.surface2 : 'transparent',
          borderBottomWidth: 1,
          borderBottomColor: colors.line,
          opacity: cat.active ? 1 : 0.55,
        })}
      >
        <Icon name={cat.icon as IconName} size={19} color={colors.gold} />
        <View style={{ flex: 1, gap: 2, minWidth: 0 }}>
          <T size={14.5} weight={700} numberOfLines={1}>
            {catLabel(cat.id)}
          </T>
          <T size={11.5} color={colors.textFaint}>
            {items(all.length)}
            {cat.active ? '' : ` · ${t('hidden_label')}`}
          </T>
        </View>
        <Icon name="edit" size={16} color={colors.textFaint} />
      </Pressable>

      <View style={{ padding: 6 }}>
        {all.length ? (
          all.map((i) => <ItemRow key={i.id} item={i} onEdit={() => onEditItem(i)} />)
        ) : (
          <T size={12.5} color={colors.textFaint} style={{ padding: 14, textAlign: 'center' }}>
            {t('no_items_here')}
          </T>
        )}

        <Pressable
          onPress={onAddItem}
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            marginTop: 4,
            paddingVertical: 11,
            borderRadius: radius.md,
            borderWidth: 1,
            borderStyle: 'dashed',
            borderColor: colors.line,
          }}
        >
          <Icon name="plus" size={15} color={colors.textDim} />
          <T size={12.5} weight={700} color={colors.textDim}>
            {t('add_item')}
          </T>
        </Pressable>
      </View>
    </View>
  );
}

/** Add, re-price and retire categories and dishes. Writes straight to the store,
 *  which queues them for upload like every other change. */
export function ManageMenuView() {
  const colors = useColors();
  const t = useT();
  const categories = useCategories(true);
  const sync = useAppStore((s) => s.sync);

  const [catEditor, setCatEditor] = useState<{ open: boolean; cat: Category | null }>({
    open: false,
    cat: null,
  });
  const [itemEditor, setItemEditor] = useState<{
    open: boolean;
    item: MenuItem | null;
    cat?: string;
  }>({ open: false, item: null });

  return (
    <View>
      {sync.state === 'signed_out' ? (
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            gap: 10,
            padding: 13,
            borderRadius: radius.md,
            borderWidth: 1,
            borderColor: colors.goldDim,
            backgroundColor: colors.surface2,
            marginBottom: 14,
          }}
        >
          <Icon name="cloudOff" size={16} color={colors.gold} />
          <T size={12.5} color={colors.textDim} style={{ flexShrink: 1 }}>
            {t('sync_signed_out')} — {t('sync_pending', { n: sync.pending })}
          </T>
        </View>
      ) : null}

      {categories.map((c) => (
        <CategoryBlock
          key={c.id}
          cat={c}
          onEditCategory={() => setCatEditor({ open: true, cat: c })}
          onAddItem={() => setItemEditor({ open: true, item: null, cat: c.id })}
          onEditItem={(item) => setItemEditor({ open: true, item })}
        />
      ))}

      <Pressable
        onPress={() => setCatEditor({ open: true, cat: null })}
        style={({ hovered }: any) => ({
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 9,
          paddingVertical: 15,
          borderRadius: radius.lg,
          borderWidth: 1,
          borderStyle: 'dashed',
          borderColor: hovered ? colors.gold : colors.line,
        })}
      >
        <Icon name="plus" size={17} color={colors.textDim} />
        <T size={13.5} weight={700} color={colors.textDim}>
          {t('add_category')}
        </T>
      </Pressable>

      <CategoryEditor
        visible={catEditor.open}
        category={catEditor.cat}
        onClose={() => setCatEditor({ open: false, cat: null })}
      />
      <ItemEditor
        visible={itemEditor.open}
        item={itemEditor.item}
        defaultCategory={itemEditor.cat}
        onClose={() => setItemEditor({ open: false, item: null })}
      />
    </View>
  );
}
